import type { Notification } from "@istari-frontend/notifications/model";
import { generatePath } from "react-router";

import { buildResourcePath, buildSystemWorkflowLogRoute, routes } from "~/routes";
import { buildSystemHomeRoute } from "~/systems/feature/system-home-route";
import { setWorkflowLogEntry } from "~/workflow-log/feature/workflow-log-route-state";

export type NotificationUrlOptions = {
  systemRedesign: boolean;
};

export function getNotificationUrl(notification: Notification, options: NotificationUrlOptions): string | null {
  switch (notification.type) {
    case "job":
      return `/jobs/${notification.payload.jobId}`;
    case "share": {
      const { resourceType, resourceId } = notification.payload;
      if (resourceType === "system") {
        return options.systemRedesign ? buildSystemHomeRoute({ systemId: resourceId }) : `/systems/${resourceId}`;
      }
      if (resourceType === "artifact") return `/resources/artifact/${resourceId}`;
      return `/resources/${resourceId}`;
    }
    case "received-resource": {
      const { event, resourceType, resourceId } = notification.payload;
      if (event === "revoked") return null;
      if (resourceType === "artifact") return buildResourcePath({ artifactId: resourceId });
      return buildResourcePath({ resourceId });
    }
    // Absolute path (not the system-scoped crPaths.detail) because the inbox is a global surface.
    case "changeRequest": {
      const { systemId, changeRequestId } = notification.payload;
      if (options.systemRedesign) {
        return buildSystemHomeRoute({ systemId, tab: "change-request-detail", changeRequestId });
      }
      return generatePath(routes.ChangeRequestDetail, { systemId, id: changeRequestId });
    }
    // A workflow log entry has no route of its own — it is a tab plus a selected entry on the
    // system details page.
    case "workflowLog": {
      const { systemId, workflowLogEntryId } = notification.payload;
      if (options.systemRedesign) return buildSystemWorkflowLogRoute(systemId, workflowLogEntryId);
      const systemPath = generatePath(routes.SystemDetails, { systemId, "*": "" });
      return `${systemPath}?${setWorkflowLogEntry(new URLSearchParams(), workflowLogEntryId)}`;
    }
  }
}
